
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Pitch, PitchFeedback, UserBadge, PitchCategory } from '@/types/pitch';

export const usePitchPlayground = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  // States
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPitch, setSelectedPitch] = useState<Pitch | null>(null);
  const [feedback, setFeedback] = useState<PitchFeedback[]>([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [userBadges, setUserBadges] = useState<UserBadge[]>([]);
  const [loadingBadges, setLoadingBadges] = useState(false);
  const [filterCategory, setFilterCategory] = useState<PitchCategory | 'all'>('all');

  // Load pitches on mount and when filter changes
  useEffect(() => {
    loadPitches();
  }, [filterCategory]);

  // Load user badges when user changes
  useEffect(() => {
    if (user) {
      loadUserBadges();
    }
  }, [user]);

  // Load pitch feedback when selected pitch changes
  useEffect(() => {
    if (selectedPitch) {
      loadPitchFeedback(selectedPitch.id);
    }
  }, [selectedPitch]);

  // Load pitches from the database
  const loadPitches = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('pitch_ideas')
        .select(`
          *,
          profiles:user_id (
            username,
            full_name,
            avatar_url
          )
        `)
        .order('created_at', { ascending: false });
        
      // Apply category filter if not "all"
      if (filterCategory !== 'all') {
        query = query.eq('category', filterCategory);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error loading pitches:', error);
        // Instead of showing an error toast to users, we just return an empty array
        setPitches([]);
        return;
      }
      
      if (data) {
        // Check if user has upvoted any of the pitches
        const pitchesWithUpvotes = await Promise.all(data.map(async (pitch) => {
          try {
            // Handle case where user might not be logged in
            if (!user?.id) {
              return {
                ...pitch,
                has_upvoted: false,
                author: {
                  username: pitch.profiles?.username,
                  full_name: pitch.profiles?.full_name,
                  avatar_url: pitch.profiles?.avatar_url
                }
              } as Pitch;
            }
            
            const { data: upvoteData, error: upvoteError } = await supabase
              .from('pitch_upvotes')
              .select('*')
              .eq('pitch_id', pitch.id)
              .eq('user_id', user.id);
              
            if (upvoteError) {
              console.error('Error checking upvote status:', upvoteError);
              return {
                ...pitch,
                has_upvoted: false,
                author: {
                  username: pitch.profiles?.username,
                  full_name: pitch.profiles?.full_name,
                  avatar_url: pitch.profiles?.avatar_url
                }
              } as Pitch;
            }
            
            const hasUpvoted = upvoteData && upvoteData.length > 0;
            
            return {
              ...pitch,
              has_upvoted: hasUpvoted,
              author: {
                username: pitch.profiles?.username,
                full_name: pitch.profiles?.full_name,
                avatar_url: pitch.profiles?.avatar_url
              }
            } as Pitch;
          } catch (err) {
            console.error('Error processing pitch:', err);
            return {
              ...pitch,
              has_upvoted: false,
              author: {
                username: pitch.profiles?.username,
                full_name: pitch.profiles?.full_name,
                avatar_url: pitch.profiles?.avatar_url
              }
            } as Pitch;
          }
        }));
        
        setPitches(pitchesWithUpvotes);
      }
    } catch (error) {
      console.error('Error loading pitches:', error);
      // Instead of showing error messages to users, we handle gracefully
      setPitches([]);
    } finally {
      setLoading(false);
    }
  };

  // Create a new pitch
  const createPitch = async (title: string, description: string, category: PitchCategory) => {
    if (!user) {
      toast({
        title: 'Login Required',
        description: 'You must be logged in to create a pitch',
        variant: 'default'
      });
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('pitch_ideas')
        .insert({
          user_id: user.id,
          title,
          description,
          category
        })
        .select();
        
      if (error) throw error;
      
      if (data) {
        // Add the new pitch to the state
        const newPitch: Pitch = {
          ...data[0],
          has_upvoted: false,
          author: {
            username: user.user_metadata?.username,
            full_name: user.user_metadata?.full_name,
            avatar_url: user.user_metadata?.avatar_url
          }
        };
        
        setPitches(prev => [newPitch, ...prev]);
        
        toast({
          title: 'Success',
          description: 'Your pitch idea has been shared!'
        });
        
        // Check if user should receive the "Idea Starter" badge
        await checkForIdeaStarterBadge();
      }
    } catch (error) {
      console.error('Error creating pitch:', error);
      toast({
        title: 'Something went wrong',
        description: 'Unable to share your pitch at the moment. Please try again later.',
        variant: 'destructive'
      });
    }
  };

  // Toggle upvote on a pitch
  const toggleUpvote = async (pitchId: string) => {
    if (!user) {
      toast({
        title: 'Login Required',
        description: 'You must be logged in to upvote',
        variant: 'default'
      });
      return;
    }
    
    try {
      // Check if user has already upvoted
      const { data: existingUpvote } = await supabase
        .from('pitch_upvotes')
        .select('*')
        .eq('pitch_id', pitchId)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (existingUpvote) {
        // User has already upvoted, so remove the upvote
        await supabase
          .from('pitch_upvotes')
          .delete()
          .eq('id', existingUpvote.id);
          
        // Update local state
        setPitches(prev => 
          prev.map(pitch => 
            pitch.id === pitchId
              ? { ...pitch, upvotes_count: pitch.upvotes_count - 1, has_upvoted: false }
              : pitch
          )
        );
        
        // If selected pitch is the one being upvoted, update it too
        if (selectedPitch?.id === pitchId) {
          setSelectedPitch(prev => 
            prev ? { ...prev, upvotes_count: prev.upvotes_count - 1, has_upvoted: false } : null
          );
        }
      } else {
        // User has not upvoted, so add an upvote
        await supabase
          .from('pitch_upvotes')
          .insert({
            pitch_id: pitchId,
            user_id: user.id
          });
          
        // Update local state
        setPitches(prev => 
          prev.map(pitch => 
            pitch.id === pitchId
              ? { ...pitch, upvotes_count: pitch.upvotes_count + 1, has_upvoted: true }
              : pitch
          )
        );
        
        // If selected pitch is the one being upvoted, update it too
        if (selectedPitch?.id === pitchId) {
          setSelectedPitch(prev => 
            prev ? { ...prev, upvotes_count: prev.upvotes_count + 1, has_upvoted: true } : null
          );
        }
        
        // Check if the pitch author should get the "Top Thinker" badge
        const { data: pitchData } = await supabase
          .from('pitch_ideas')
          .select('user_id, upvotes_count')
          .eq('id', pitchId)
          .single();
          
        if (pitchData && pitchData.upvotes_count + 1 >= 10) {
          await checkForTopThinkerBadge(pitchData.user_id);
        }
      }
    } catch (error) {
      console.error('Error toggling upvote:', error);
      // Don't show error messages to users
    }
  };

  // Load feedback for a specific pitch
  const loadPitchFeedback = async (pitchId: string) => {
    try {
      setLoadingFeedback(true);
      
      const { data, error } = await supabase
        .from('pitch_feedback')
        .select(`
          *,
          profiles:user_id (
            username,
            full_name,
            avatar_url
          )
        `)
        .eq('pitch_id', pitchId)
        .order('created_at', { ascending: true });
        
      if (error) {
        console.error('Error loading feedback:', error);
        setFeedback([]);
        return;
      }
      
      if (data) {
        const formattedFeedback = data.map(item => ({
          ...item,
          author: {
            username: item.profiles?.username,
            full_name: item.profiles?.full_name,
            avatar_url: item.profiles?.avatar_url
          }
        })) as PitchFeedback[];
        
        setFeedback(formattedFeedback);
      }
    } catch (error) {
      console.error('Error loading feedback:', error);
      // Handle errors gracefully without showing messages to users
      setFeedback([]);
    } finally {
      setLoadingFeedback(false);
    }
  };

  // Add feedback to a pitch
  const addFeedback = async (pitchId: string, content: string) => {
    if (!user) {
      toast({
        title: 'Login Required',
        description: 'You must be logged in to provide feedback',
        variant: 'default'
      });
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('pitch_feedback')
        .insert({
          pitch_id: pitchId,
          user_id: user.id,
          content
        })
        .select();
        
      if (error) throw error;
      
      if (data) {
        // Add the new feedback to the state
        const newFeedback: PitchFeedback = {
          ...data[0],
          author: {
            username: user.user_metadata?.username,
            full_name: user.user_metadata?.full_name,
            avatar_url: user.user_metadata?.avatar_url
          }
        };
        
        setFeedback(prev => [...prev, newFeedback]);
        
        toast({
          title: 'Success',
          description: 'Your feedback has been shared!'
        });
      }
    } catch (error) {
      console.error('Error adding feedback:', error);
      toast({
        title: 'Something went wrong',
        description: 'Unable to submit your feedback at the moment. Please try again later.',
        variant: 'destructive'
      });
    }
  };

  // Mark feedback as helpful
  const markFeedbackAsHelpful = async (feedbackId: string) => {
    if (!user || !selectedPitch) return;
    
    // Only the pitch owner can mark feedback as helpful
    if (selectedPitch.user_id !== user.id) {
      toast({
        title: 'Not authorized',
        description: 'Only the pitch owner can mark feedback as helpful',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      const { error } = await supabase
        .from('pitch_feedback')
        .update({ is_helpful: true })
        .eq('id', feedbackId);
        
      if (error) throw error;
      
      // Update local state
      setFeedback(prev => 
        prev.map(item => 
          item.id === feedbackId
            ? { ...item, is_helpful: true }
            : item
        )
      );
      
      // Get the feedback author to award badge
      const { data: feedbackData } = await supabase
        .from('pitch_feedback')
        .select('user_id')
        .eq('id', feedbackId)
        .single();
        
      if (feedbackData) {
        await checkForCollabChampBadge(feedbackData.user_id);
      }
      
      toast({
        title: 'Success',
        description: 'Feedback marked as helpful!'
      });
    } catch (error) {
      console.error('Error marking feedback as helpful:', error);
      // Don't show error messages to users
    }
  };

  // Load user badges
  const loadUserBadges = async () => {
    if (!user) return;
    
    try {
      setLoadingBadges(true);
      
      const { data, error } = await supabase
        .from('user_badges')
        .select('*')
        .eq('user_id', user.id);
        
      if (error) {
        console.error('Error loading user badges:', error);
        setUserBadges([]);
        return;
      }
      
      if (data) {
        setUserBadges(data as UserBadge[]);
      }
    } catch (error) {
      console.error('Error loading user badges:', error);
      setUserBadges([]);
    } finally {
      setLoadingBadges(false);
    }
  };

  // Check if user should get the "Idea Starter" badge
  const checkForIdeaStarterBadge = async () => {
    if (!user) return;
    
    try {
      // Check if user already has the badge
      const { data: existingBadge, error } = await supabase
        .from('user_badges')
        .select('*')
        .eq('user_id', user.id)
        .eq('badge_type', 'idea_starter')
        .maybeSingle();
        
      if (error) {
        console.error('Error checking for badge:', error);
        return;
      }
        
      if (!existingBadge) {
        // User doesn't have the badge, so award it
        const { data, error: insertError } = await supabase
          .from('user_badges')
          .insert({
            user_id: user.id,
            badge_type: 'idea_starter'
          })
          .select();
          
        if (insertError) {
          console.error('Error awarding badge:', insertError);
          return;
        }
        
        if (data) {
          // Add the new badge to the state
          setUserBadges(prev => [...prev, data[0] as UserBadge]);
          
          toast({
            title: 'Badge Earned!',
            description: 'You earned the "Idea Starter" badge!'
          });
        }
      }
    } catch (error) {
      console.error('Error checking for Idea Starter badge:', error);
    }
  };

  // Check if user should get the "Top Thinker" badge
  const checkForTopThinkerBadge = async (userId: string) => {
    try {
      // Check if user already has the badge
      const { data: existingBadge, error } = await supabase
        .from('user_badges')
        .select('*')
        .eq('user_id', userId)
        .eq('badge_type', 'top_thinker')
        .maybeSingle();
        
      if (error) {
        console.error('Error checking for badge:', error);
        return;
      }
        
      if (!existingBadge) {
        // User doesn't have the badge, so award it
        const { error: insertError } = await supabase
          .from('user_badges')
          .insert({
            user_id: userId,
            badge_type: 'top_thinker'
          });
          
        if (insertError) {
          console.error('Error awarding badge:', insertError);
          return;
        }
          
        // Only show toast if it's the current user
        if (user?.id === userId) {
          toast({
            title: 'Badge Earned!',
            description: 'You earned the "Top Thinker" badge!'
          });
          
          // Refresh user badges if it's the current user
          loadUserBadges();
        }
      }
    } catch (error) {
      console.error('Error checking for Top Thinker badge:', error);
    }
  };

  // Check if user should get the "Collab Champ" badge
  const checkForCollabChampBadge = async (userId: string) => {
    try {
      // Check if user already has the badge
      const { data: existingBadge, error } = await supabase
        .from('user_badges')
        .select('*')
        .eq('user_id', userId)
        .eq('badge_type', 'collab_champ')
        .maybeSingle();
        
      if (error) {
        console.error('Error checking for badge:', error);
        return;
      }
        
      if (!existingBadge) {
        // Check how many helpful feedback the user has given
        const { count, error: countError } = await supabase
          .from('pitch_feedback')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_helpful', true);
          
        if (countError) {
          console.error('Error counting helpful feedback:', countError);
          return;
        }
          
        if ((count || 0) >= 3) {
          // User has 3 or more helpful feedback, award the badge
          const { error: insertError } = await supabase
            .from('user_badges')
            .insert({
              user_id: userId,
              badge_type: 'collab_champ'
            });
            
          if (insertError) {
            console.error('Error awarding badge:', insertError);
            return;
          }
            
          // Only show toast if it's the current user
          if (user?.id === userId) {
            toast({
              title: 'Badge Earned!',
              description: 'You earned the "Collab Champ" badge!'
            });
            
            // Refresh user badges if it's the current user
            loadUserBadges();
          }
        }
      }
    } catch (error) {
      console.error('Error checking for Collab Champ badge:', error);
    }
  };

  return {
    pitches,
    loading,
    createPitch,
    toggleUpvote,
    selectedPitch,
    setSelectedPitch,
    feedback,
    loadingFeedback,
    addFeedback,
    markFeedbackAsHelpful,
    userBadges,
    loadingBadges,
    filterCategory,
    setFilterCategory
  };
};
